/**
 * Offer-based retailer fallback (client mirror of supabase/functions/_shared/offerRetailer.ts).
 *
 * Campaign-offer vouchers carry `offer_id` but often no voucher-level retailer fields, so facility→org
 * enrichment falls through to blank/"Unknown". This resolves the retailer from the offer's CONFIGURED scope,
 * but only when unambiguous, and only as a fallback AFTER actual redemption data. See the edge-fn copy for
 * the precedence + scope rules. Keep the two in sync.
 */
import { supabase } from "../services/supabase";

export type RetailerSource =
  | "actual_redemption_location"
  | "redeeming_organization"
  | "voucher_facility"
  | "event_promoter"
  | "offer_single_organization_fallback"
  | "unknown";

export interface OfferRetailer {
  scope: string;
  orgName: string | null;
  locationName: string | null;
  facilityId: string | null;
  ambiguous: boolean;
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Pure scope resolution (no DB) — the testable core of the fallback.
 *   SINGLE_ORGANIZATION       → the one configured org (+ location if set).
 *   MULTIPLE_ORGANIZATIONS     → only if EXACTLY ONE org configured (unambiguous); else ambiguous, no infer.
 *   ALL_ORGANIZATIONS / other  → never infer a retailer.
 */
export function resolveOfferRetailerRows(
  scope: string,
  rows: any[],
): OfferRetailer {
  if (scope === "ALL_ORGANIZATIONS") {
    return {
      scope,
      orgName: null,
      locationName: null,
      facilityId: null,
      ambiguous: false,
    };
  }
  const isSingle =
    scope === "SINGLE_ORGANIZATION" ||
    (scope === "MULTIPLE_ORGANIZATIONS" && rows.length === 1);
  if (isSingle && rows.length >= 1) {
    const r = rows[0];
    return {
      scope,
      orgName: r.organizations?.name ?? null,
      locationName: r.locations?.name ?? null,
      facilityId:
        r.locations?.facility_id ?? r.organizations?.facility_id ?? null,
      ambiguous: false,
    };
  }
  return {
    scope,
    orgName: null,
    locationName: null,
    facilityId: null,
    ambiguous: rows.length > 1,
  };
}

/** Build offer_id -> OfferRetailer for the given offers (only unambiguous single-org offers yield a retailer). */
export async function buildOfferRetailerMap(
  offerIds: string[],
  /** Called with each lookup error (the affected offers then resolve to no retailer). */
  onLookupError?: (error: unknown) => void,
): Promise<Map<string, OfferRetailer>> {
  const map = new Map<string, OfferRetailer>();
  const ids = Array.from(new Set(offerIds.filter(Boolean)));
  if (ids.length === 0) {
    return map;
  }

  const { data: offers, error: offersError } = await supabase
    .from("offers")
    .select("id, redemption_scope")
    .in("id", ids);
  if (offersError) {
    onLookupError?.(offersError);
  }
  const scopeById = new Map<string, string>();
  (offers || []).forEach((o: any) => scopeById.set(o.id, o.redemption_scope));

  const { data: oos, error: oosError } = await supabase
    .from("offer_organizations")
    .select(
      "offer_id, organization_id, location_id, organizations(name, facility_id), locations(name, facility_id)",
    )
    .in("offer_id", ids);
  if (oosError) {
    onLookupError?.(oosError);
  }

  const byOffer = new Map<string, any[]>();
  (oos || []).forEach((row: any) => {
    const arr = byOffer.get(row.offer_id) || [];
    arr.push(row);
    byOffer.set(row.offer_id, arr);
  });

  for (const offerId of ids) {
    map.set(
      offerId,
      resolveOfferRetailerRows(
        scopeById.get(offerId) || "",
        byOffer.get(offerId) || [],
      ),
    );
  }
  return map;
}
